import React, { useEffect, useRef } from "react";

/**
 * Deep-space starfield rendered on <canvas> with:
 *  - three parallax layers of stars
 *  - occasional twinkles + subtle drift
 *  - shooting stars every 4-9s
 * All layered under a soft red vignette matching the tactical accent.
 */
export default function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let width = 0;
    let height = 0;
    let stars = [];
    let shooters = [];
    let lastShoot = 0;

    const rand = (a, b) => a + Math.random() * (b - a);

    function makeStars() {
      const count = Math.floor((width * height) / 8500);
      stars = new Array(count).fill(0).map(() => {
        // Bigger, brighter stars — layer 2 is now the dominant class
        const r = Math.random();
        const layer = r < 0.35 ? 0 : r < 0.75 ? 1 : 2;
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          r: layer === 2 ? rand(1.8, 3.2) : layer === 1 ? rand(1.0, 1.8) : rand(0.4, 1.0),
          a: rand(0.55, 1),
          twinkle: rand(0.006, 0.022),
          phase: Math.random() * Math.PI * 2,
          drift: layer === 2 ? 0.28 : layer === 1 ? 0.14 : 0.06,
          layer,
          cross: layer === 2 && Math.random() < 0.45,
          glow: layer === 2 ? rand(6, 14) : 0,
        };
      });
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      makeStars();
    }

    function drawStar(s) {
      const alpha = s.a * (0.6 + 0.4 * Math.sin(s.phase));
      // Glow halo for bright stars
      if (s.glow && alpha > 0.35) {
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.glow);
        g.addColorStop(0, `rgba(255,255,255,${0.35 * alpha})`);
        g.addColorStop(0.4, `rgba(255,255,255,${0.08 * alpha})`);
        g.addColorStop(1, "rgba(255,255,255,0)");
        ctx.globalAlpha = 1;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.glow, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = Math.max(0.05, alpha);
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
      // Cross flare for brightest ones
      if (s.cross && alpha > 0.6) {
        ctx.globalAlpha = (alpha - 0.5) * 1.1;
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 0.7;
        const len = s.r * 8;
        ctx.beginPath();
        ctx.moveTo(s.x - len, s.y);
        ctx.lineTo(s.x + len, s.y);
        ctx.moveTo(s.x, s.y - len);
        ctx.lineTo(s.x, s.y + len);
        ctx.stroke();
      }
    }

    function spawnShooter() {
      const fromLeft = Math.random() < 0.5;
      shooters.push({
        x: fromLeft ? -50 : width + 50,
        y: rand(0, height * 0.6),
        vx: fromLeft ? rand(10, 15) : rand(-15, -10),
        vy: rand(2.5, 5),
        life: 1,
        trail: [],
      });
    }

    function tick(t) {
      ctx.clearRect(0, 0, width, height);
      // background gradient
      const grd = ctx.createRadialGradient(width * 0.5, height * 0.4, 40, width * 0.5, height * 0.5, Math.max(width, height));
      grd.addColorStop(0, "#050508");
      grd.addColorStop(0.6, "#020204");
      grd.addColorStop(1, "#000000");
      ctx.globalAlpha = 1;
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, width, height);

      for (const s of stars) {
        s.phase += s.twinkle;
        s.x += s.drift * 0.35;
        s.y += s.drift * 0.12;
        if (s.x > width + 5) s.x = -5;
        if (s.y > height + 5) s.y = -5;
        drawStar(s);
      }

      if (t - lastShoot > rand(2200, 5200)) {
        spawnShooter();
        lastShoot = t;
      }

      for (const sh of shooters) {
        sh.trail.push([sh.x, sh.y]);
        if (sh.trail.length > 14) sh.trail.shift();
        sh.x += sh.vx;
        sh.y += sh.vy;
        sh.life -= 0.012;
        // trail
        for (let i = 0; i < sh.trail.length; i++) {
          const [tx, ty] = sh.trail[i];
          ctx.globalAlpha = (i / sh.trail.length) * sh.life * 0.9;
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(tx, ty, 1.4 * (i / sh.trail.length), 0, Math.PI * 2);
          ctx.fill();
        }
        // head
        ctx.globalAlpha = sh.life;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      shooters = shooters.filter((s) => s.life > 0 && s.x > -100 && s.x < width + 100);

      raf = requestAnimationFrame(tick);
    }

    resize();
    window.addEventListener("resize", resize);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="gc-starfield" aria-hidden="true" />
      <div className="gc-vignette" aria-hidden="true" />
      <div className="gc-nebula" aria-hidden="true" />
    </>
  );
}
